import type { FilterNode } from '@ofmos/shared/filter-ast';
import type { EvaluationContext, EvaluatorField } from '@ofmos/shared/filter-eval';
import { evaluateFilter, FilterEvaluationError } from '@ofmos/shared/filter-eval';
import { FilterOperandError } from '@ofmos/shared/filter-operand';
import type { FieldMetadata, RecordRow } from './client';

/**
 * INV-6 ONE FILTER SEMANTICS, at the browser's call site.
 *
 * The evaluator itself lives in `shared` and is the same one the conformance corpus runs
 * against the SQL compiler. This file only translates the UI's field metadata into what it
 * expects, so there is still exactly one implementation of "does this record match".
 *
 * Two callers, deliberately different about failure:
 *  - the fixture backend calls `evaluateFilter` directly and lets a bad filter throw, which
 *    is what makes a wrong filter visible in a test;
 *  - the optimistic path uses `recordStillMatches`, which KEEPS the record when the filter
 *    cannot be evaluated - an unknown field id (`FilterEvaluationError`) or an operand the
 *    normalizer refuses (`FilterOperandError`, e.g. a filter written against option LABELS).
 *    Hiding a row because the browser could not decide would be a change the server never
 *    agreed to, and throwing here would break the write's success path over a display
 *    question. The next refetch settles it either way.
 */
export function evaluatorFields(
  fields: readonly FieldMetadata[],
): ReadonlyMap<string, EvaluatorField> {
  const map = new Map<string, EvaluatorField>();
  for (const field of fields) {
    const options = field.options?.selectOptions;
    map.set(field.id, {
      type: field.type,
      ...(options === undefined
        ? {}
        : {
            options: [...options]
              .sort((a, b) => a.position - b.position)
              .map((option) => ({
                id: option.id,
                label: option.label,
                ...(option.color === undefined ? {} : { color: option.color }),
              })),
          }),
    });
  }
  return map;
}

export function evaluationContext(
  fields: readonly FieldMetadata[],
  values: Readonly<Record<string, unknown>>,
): EvaluationContext {
  return { fields: evaluatorFields(fields), values };
}

/**
 * Does an EDITED record still belong in a filtered list?
 *
 * This is the question a filtered table has to answer without a round trip: a record whose
 * stage was just changed away from the filtered value must leave the list, or the user
 * stares at a row that the server would no longer return.
 */
export function recordStillMatches(
  filter: FilterNode | undefined,
  fields: readonly FieldMetadata[],
  record: RecordRow,
): boolean {
  if (filter === undefined) return true;
  try {
    return evaluateFilter(filter, evaluationContext(fields, record.values));
  } catch (error) {
    if (error instanceof FilterEvaluationError || error instanceof FilterOperandError) return true;
    throw error;
  }
}
